import React, { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import {
  Bars3Icon,
  BriefcaseIcon,
  ChartBarIcon,
  MoonIcon,
  SunIcon,
  UsersIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'
import { useAuth } from '../../contextos/autenticacion/ContextoAutenticacion'
import { useTema } from '../../contextos/tema/ContextoTema'

interface LayoutEmpresaProps {
  children: React.ReactNode
}

const LayoutEmpresa: React.FC<LayoutEmpresaProps> = ({ children }) => {
  const location = useLocation()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const { usuario, logout } = useAuth()
  const { toggleTema, isDark } = useTema()

  const esGestion =
    usuario?.tipo_organizacion === 'empresa' &&
    ['admin', 'moderador', 'supervisor', 'contador'].includes(usuario?.rol_organizacion || '')
  const navItems = esGestion
    ? [
        { name: 'Dashboard', href: '/empresa/gestion', icon: ChartBarIcon },
        { name: 'Miembros y Equipos', href: '/empresa/miembros', icon: UsersIcon },
      ]
    : [{ name: 'Dashboard', href: '/empresa', icon: ChartBarIcon }]

  const isCurrentPath = (href: string) => location.pathname === href

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="fixed left-0 right-0 top-0 z-40 flex items-center justify-between bg-white/80 px-4 py-3 shadow backdrop-blur dark:bg-gray-800/80 lg:hidden">
        <div className="flex items-center space-x-2">
          <BriefcaseIcon className="h-6 w-6 text-emerald-600 dark:text-emerald-400" />
          <span className="font-semibold text-gray-900 dark:text-white">Panel Empresa</span>
        </div>
        <div className="flex items-center gap-2">
          {usuario && <span className="text-sm text-gray-700 dark:text-gray-200">{usuario.nombre}</span>}
          <button
            onClick={() => setSidebarOpen((valor) => !valor)}
            className="rounded-md p-2 text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700"
          >
            {sidebarOpen ? <XMarkIcon className="h-6 w-6" /> : <Bars3Icon className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-gray-900/40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <div className="flex">
        <aside
          className={`fixed left-0 top-0 z-50 h-[calc(100vh)] w-56 transform border-r border-gray-200 bg-white transition-transform duration-200 dark:border-gray-700 dark:bg-gray-800 lg:static lg:h-screen ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}
        >
          <div className="hidden items-center space-x-2 px-4 py-4 lg:flex">
            <BriefcaseIcon className="h-7 w-7 text-emerald-600 dark:text-emerald-400" />
            <span className="font-semibold text-gray-900 dark:text-white">Panel Empresa</span>
          </div>

          <nav className="mt-2 px-2">
            {navItems.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                className={`group mb-1 flex items-center gap-3 rounded-lg px-3 py-2 transition ${isCurrentPath(item.href) ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-200' : 'text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700'}`}
                onClick={() => setSidebarOpen(false)}
              >
                <item.icon className={`h-5 w-5 ${isCurrentPath(item.href) ? 'text-emerald-600 dark:text-emerald-300' : 'text-gray-500 dark:text-gray-400'}`} />
                <span className="truncate">{item.name}</span>
              </Link>
            ))}
          </nav>
        </aside>

        <main className="flex-1 pt-14 lg:pt-0">
          <div className="hidden items-center justify-between border-b border-gray-200 bg-transparent px-4 py-2 dark:border-gray-800 lg:flex">
            <div className="flex items-center gap-2">
              <BriefcaseIcon className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
              <span className="text-sm font-medium text-gray-900 dark:text-white">Panel Empresa</span>
            </div>
            <div className="flex items-center gap-4">
              <button
                onClick={toggleTema}
                className="rounded-lg p-2 text-gray-500 transition-colors hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700"
                title={isDark ? 'Cambiar a modo claro' : 'Cambiar a modo oscuro'}
              >
                {isDark ? <SunIcon className="h-5 w-5" /> : <MoonIcon className="h-5 w-5" />}
              </button>
              {usuario && <span className="text-sm text-gray-700 dark:text-gray-200">{usuario.nombre}</span>}
              <button onClick={logout} className="rounded bg-gray-900 px-3 py-1 text-sm text-white dark:bg-gray-700">
                Cerrar sesion
              </button>
            </div>
          </div>

          <div className="px-4 py-4 lg:px-6">{children}</div>
        </main>
      </div>
    </div>
  )
}

export default LayoutEmpresa
